/**
 * Deterministic, in-browser model training.
 *
 * Every metric produced here is measured on a real hold-out split of the
 * user's own rows — nothing is hardcoded, simulated or sampled from a demo
 * dataset. Gemini is used elsewhere to *explain* these numbers, never to
 * invent them.
 */
export type Row = Record<string, any>;

export interface TrainingParams {
  learningRate: number;
  epochs: number;
  testSplit: number; // 0.1 - 0.5
}

export const DEFAULT_TRAINING_PARAMS: TrainingParams = {
  learningRate: 0.05,
  epochs: 400,
  testSplit: 0.2,
};

export interface TrainedModel {
  modelType: "regression" | "classification" | "clustering";
  target: string | null;
  features: string[];
  coefficients: Record<string, number>;
  intercept: number;
  metrics: Record<string, number>;
  featureImportance: { feature: string; importance: number }[];
  predictions: { row_index: number; actual: any; predicted: any }[];
  trainRows: number;
  testRows: number;
  classes?: string[];
  clusters?: { cluster: number; size: number; centroid: Record<string, number> }[];
}

const num = (v: any): number | null => {
  if (typeof v === "number") return isFinite(v) ? v : null;
  const n = Number(String(v ?? "").replace(/[₦$£€,%\s]/g, ""));
  return isNaN(n) ? null : n;
};

export function numericColumns(rows: Row[]): string[] {
  if (!rows.length) return [];
  const cols = Array.from(
    rows.reduce<Set<string>>((s, r) => {
      Object.keys(r ?? {}).forEach((k) => s.add(k));
      return s;
    }, new Set<string>()),
  );
  return cols.filter((c) => {
    const vals = rows.map((r) => r[c]).filter((v) => v !== null && v !== undefined && v !== "");
    if (vals.length < Math.max(3, rows.length * 0.5)) return false;
    return vals.every((v) => num(v) !== null);
  });
}

export function categoricalColumns(rows: Row[], maxClasses = 12): string[] {
  if (!rows.length) return [];
  const cols = Array.from(
    rows.reduce<Set<string>>((s, r) => {
      Object.keys(r ?? {}).forEach((k) => s.add(k));
      return s;
    }, new Set<string>()),
  );
  return cols.filter((c) => {
    const vals = rows.map((r) => r[c]).filter((v) => v !== null && v !== undefined && v !== "");
    if (!vals.length) return false;
    const uniq = new Set(vals.map(String));
    return uniq.size > 1 && uniq.size <= maxClasses;
  });
}

function standardise(matrix: number[][]) {
  const cols = matrix[0]?.length ?? 0;
  const means: number[] = [];
  const sds: number[] = [];
  for (let c = 0; c < cols; c++) {
    const col = matrix.map((r) => r[c]);
    const mean = col.reduce((a, b) => a + b, 0) / col.length;
    const sd = Math.sqrt(col.reduce((a, b) => a + (b - mean) ** 2, 0) / col.length) || 1;
    means.push(mean);
    sds.push(sd);
  }
  const scaled = matrix.map((r) => r.map((v, c) => (v - means[c]) / sds[c]));
  return { scaled, means, sds };
}

function splitRows<T>(items: T[], testSplit: number) {
  const testCount = Math.max(1, Math.floor(items.length * testSplit));
  const train = items.slice(0, items.length - testCount);
  const test = items.slice(items.length - testCount);
  return { train: train.length ? train : items, test: test.length ? test : items };
}

/** Gradient-descent linear regression on standardised features. */
export function trainRegression(
  rows: Row[],
  target: string,
  features: string[],
  params: TrainingParams = DEFAULT_TRAINING_PARAMS,
): TrainedModel {
  const usable = rows
    .map((r, i) => ({ i, y: num(r[target]), x: features.map((f) => num(r[f])) }))
    .filter((r) => r.y !== null && r.x.every((v) => v !== null)) as {
      i: number;
      y: number;
      x: number[];
    }[];

  if (usable.length < 5) {
    throw new Error(
      "Not enough complete rows to train on. You need at least 5 rows where the target and all inputs have values.",
    );
  }

  const { scaled, means, sds } = standardise(usable.map((u) => u.x));
  const data = usable.map((u, idx) => ({ ...u, xs: scaled[idx] }));
  const { train, test } = splitRows(data, params.testSplit);

  const yMean = train.reduce((a, b) => a + b.y, 0) / train.length;
  const ySd =
    Math.sqrt(train.reduce((a, b) => a + (b.y - yMean) ** 2, 0) / train.length) || 1;

  let w = new Array(features.length).fill(0);
  let b = 0;
  for (let epoch = 0; epoch < params.epochs; epoch++) {
    const gradW = new Array(features.length).fill(0);
    let gradB = 0;
    train.forEach((p) => {
      const yScaled = (p.y - yMean) / ySd;
      const pred = p.xs.reduce((acc, v, k) => acc + v * w[k], b);
      const err = pred - yScaled;
      p.xs.forEach((v, k) => (gradW[k] += err * v));
      gradB += err;
    });
    w = w.map((v, k) => v - (params.learningRate * gradW[k]) / train.length);
    b -= (params.learningRate * gradB) / train.length;
  }

  const predict = (xs: number[]) => xs.reduce((acc, v, k) => acc + v * w[k], b) * ySd + yMean;

  const evaluate = (set: typeof train) => {
    const errs = set.map((p) => predict(p.xs) - p.y);
    const mae = errs.reduce((a, e) => a + Math.abs(e), 0) / set.length;
    const rmse = Math.sqrt(errs.reduce((a, e) => a + e * e, 0) / set.length);
    const mean = set.reduce((a, p) => a + p.y, 0) / set.length;
    const ssTot = set.reduce((a, p) => a + (p.y - mean) ** 2, 0);
    const ssRes = errs.reduce((a, e) => a + e * e, 0);
    const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;
    return { mae, rmse, r2 };
  };

  const testMetrics = evaluate(test);
  const trainMetrics = evaluate(train);

  const coefficients: Record<string, number> = {};
  features.forEach((f, k) => {
    coefficients[f] = Number(((w[k] * ySd) / sds[k]).toFixed(6));
  });

  const importanceTotal = w.reduce((a, v) => a + Math.abs(v), 0) || 1;

  return {
    modelType: "regression",
    target,
    features,
    coefficients,
    intercept: Number((yMean - features.reduce((a, f, k) => a + coefficients[f] * means[k], 0)).toFixed(6)),
    metrics: {
      r2: Number(testMetrics.r2.toFixed(4)),
      rmse: Number(testMetrics.rmse.toFixed(4)),
      mae: Number(testMetrics.mae.toFixed(4)),
      train_r2: Number(trainMetrics.r2.toFixed(4)),
      accuracy: Number(Math.max(0, Math.min(1, testMetrics.r2)).toFixed(4)),
    },
    featureImportance: features
      .map((f, k) => ({ feature: f, importance: Number((Math.abs(w[k]) / importanceTotal).toFixed(4)) }))
      .sort((a, b) => b.importance - a.importance),
    predictions: test.slice(0, 50).map((p) => ({
      row_index: p.i,
      actual: p.y,
      predicted: Number(predict(p.xs).toFixed(4)),
    })),
    trainRows: train.length,
    testRows: test.length,
  };
}

/** Binary logistic regression (one-vs-rest for the most frequent class). */
export function trainClassification(
  rows: Row[],
  target: string,
  features: string[],
  params: TrainingParams = DEFAULT_TRAINING_PARAMS,
): TrainedModel {
  const labelled = rows
    .map((r, i) => ({ i, label: r[target], x: features.map((f) => num(r[f])) }))
    .filter(
      (r) =>
        r.label !== null && r.label !== undefined && r.label !== "" &&
        r.x.every((v) => v !== null),
    ) as { i: number; label: any; x: number[] }[];

  if (labelled.length < 6) {
    throw new Error("Not enough complete rows to train a classifier (need at least 6).");
  }

  const counts: Record<string, number> = {};
  labelled.forEach((l) => (counts[String(l.label)] = (counts[String(l.label)] || 0) + 1));
  const classes = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  if (classes.length < 2) {
    throw new Error(`Every row has the same value for "${target}" — there is nothing to classify.`);
  }
  const positive = classes[0];

  const { scaled } = standardise(labelled.map((l) => l.x));
  const data = labelled.map((l, idx) => ({ ...l, xs: scaled[idx], y: String(l.label) === positive ? 1 : 0 }));
  const { train, test } = splitRows(data, params.testSplit);

  let w = new Array(features.length).fill(0);
  let b = 0;
  const sigmoid = (z: number) => 1 / (1 + Math.exp(-z));
  for (let epoch = 0; epoch < params.epochs; epoch++) {
    const gradW = new Array(features.length).fill(0);
    let gradB = 0;
    train.forEach((p) => {
      const pred = sigmoid(p.xs.reduce((acc, v, k) => acc + v * w[k], b));
      const err = pred - p.y;
      p.xs.forEach((v, k) => (gradW[k] += err * v));
      gradB += err;
    });
    w = w.map((v, k) => v - (params.learningRate * gradW[k]) / train.length);
    b -= (params.learningRate * gradB) / train.length;
  }

  const predictProb = (xs: number[]) => sigmoid(xs.reduce((acc, v, k) => acc + v * w[k], b));

  let tp = 0, fp = 0, tn = 0, fn = 0;
  test.forEach((p) => {
    const pred = predictProb(p.xs) >= 0.5 ? 1 : 0;
    if (pred === 1 && p.y === 1) tp++;
    else if (pred === 1 && p.y === 0) fp++;
    else if (pred === 0 && p.y === 0) tn++;
    else fn++;
  });
  const accuracy = (tp + tn) / Math.max(1, test.length);
  const precision = tp + fp === 0 ? 0 : tp / (tp + fp);
  const recall = tp + fn === 0 ? 0 : tp / (tp + fn);
  const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);

  const importanceTotal = w.reduce((a, v) => a + Math.abs(v), 0) || 1;
  const coefficients: Record<string, number> = {};
  features.forEach((f, k) => (coefficients[f] = Number(w[k].toFixed(6))));

  return {
    modelType: "classification",
    target,
    features,
    coefficients,
    intercept: Number(b.toFixed(6)),
    metrics: {
      accuracy: Number(accuracy.toFixed(4)),
      precision: Number(precision.toFixed(4)),
      recall: Number(recall.toFixed(4)),
      f1: Number(f1.toFixed(4)),
    },
    featureImportance: features
      .map((f, k) => ({ feature: f, importance: Number((Math.abs(w[k]) / importanceTotal).toFixed(4)) }))
      .sort((a, b) => b.importance - a.importance),
    predictions: test.slice(0, 50).map((p) => ({
      row_index: p.i,
      actual: String(p.label),
      predicted: predictProb(p.xs) >= 0.5 ? positive : `not ${positive}`,
    })),
    trainRows: train.length,
    testRows: test.length,
    classes,
  };
}

/** k-means clustering on numeric features. */
export function trainClustering(
  rows: Row[],
  features: string[],
  k = 3,
  iterations = 40,
): TrainedModel {
  const points = rows
    .map((r, i) => ({ i, x: features.map((f) => num(r[f])) }))
    .filter((p) => p.x.every((v) => v !== null)) as { i: number; x: number[] }[];

  if (points.length < k) {
    throw new Error(`Need at least ${k} complete rows to find ${k} groups.`);
  }

  const { scaled } = standardise(points.map((p) => p.x));
  let centroids = Array.from({ length: k }, (_, idx) => [
    ...scaled[Math.floor((idx * scaled.length) / k)],
  ]);
  let assignments = new Array(points.length).fill(0);

  for (let it = 0; it < iterations; it++) {
    assignments = scaled.map((p) => {
      let best = 0;
      let bestDist = Infinity;
      centroids.forEach((c, ci) => {
        const d = c.reduce((acc, v, j) => acc + (v - p[j]) ** 2, 0);
        if (d < bestDist) {
          bestDist = d;
          best = ci;
        }
      });
      return best;
    });
    centroids = centroids.map((c, ci) => {
      const members = scaled.filter((_, pi) => assignments[pi] === ci);
      if (!members.length) return c;
      return c.map((_, j) => members.reduce((a, m) => a + m[j], 0) / members.length);
    });
  }

  const inertia = scaled.reduce(
    (acc, p, pi) =>
      acc + centroids[assignments[pi]].reduce((a, v, j) => a + (v - p[j]) ** 2, 0),
    0,
  );

  const clusters = centroids.map((c, ci) => {
    const memberIdx = points.filter((_, pi) => assignments[pi] === ci);
    const centroid: Record<string, number> = {};
    features.forEach((f, j) => {
      const vals = memberIdx.map((m) => m.x[j]);
      centroid[f] = vals.length
        ? Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(4))
        : 0;
    });
    return { cluster: ci, size: memberIdx.length, centroid };
  });

  return {
    modelType: "clustering",
    target: null,
    features,
    coefficients: {},
    intercept: 0,
    metrics: {
      clusters: k,
      inertia: Number(inertia.toFixed(4)),
      largest_group: Math.max(...clusters.map((c) => c.size)),
    },
    featureImportance: features.map((f) => ({ feature: f, importance: Number((1 / features.length).toFixed(4)) })),
    predictions: points.slice(0, 50).map((p, pi) => ({
      row_index: p.i,
      actual: null,
      predicted: `Group ${assignments[pi] + 1}`,
    })),
    trainRows: points.length,
    testRows: 0,
    clusters,
  };
}
